/* eslint-disable @typescript-eslint/no-unused-vars */
// PrinterComponent.tsx
import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { StarPRNT, Emulation } from '@awesome-cordova-plugins/star-prnt';

import { checkBluetoothPermission } from 'src/hooks/use-device-permissions';

import { tryWithLock } from 'src/utils/printer-lock';

import { usePrinterStore } from 'src/store/PrinterStore';

export function PrinterPlugin() {
  const { setPrinterIsReadyWithStatus, setIsPrinterReady } = usePrinterStore();

  useEffect(() => {
    let connectionCheckInterval: NodeJS.Timeout | null = null;

    const connectToPrinter = async () => {
      // Acquire the printer lock non-blocking — if a print is in progress, skip this cycle
      const result = await tryWithLock(async () => {
        if (Capacitor.isNativePlatform()) {
          const btPermission = await checkBluetoothPermission();
          if (btPermission !== 'granted') {
            console.log('[Printer] Skipping discovery - Bluetooth permission not granted');
            setIsPrinterReady(false);
            return;
          }
        }

        const port = 'BT:TSP100';
        const emulation = Emulation.StarGraphic;

        console.log('[Printer] Checking if printers are available...');
        const printers = await StarPRNT.portDiscovery('All');
        console.log('[Printer] Found printers:', printers);

        if (printers.length > 0) {
          const btPrinters = printers.filter((p: any) => p.portName?.startsWith('BT:'));
          const tsp100Printer =
            btPrinters.find((p: any) => p.portName === port) ||
            btPrinters[0] ||
            printers.find((p: any) => p.portName === port) ||
            printers[0];
          console.log('[Printer] Using printer:', tsp100Printer);

          const printerPort = tsp100Printer?.portName || port;
          if (printerPort.startsWith('TCP:')) {
            console.log('[Printer] Skipping status check for TCP printer (SDK bug workaround)');
            setIsPrinterReady(false);
            return;
          }

          const status = await StarPRNT.checkStatus(printerPort, emulation);
          console.log('[Printer] Printer status:', status);

          if (!status.offline && !status.coverOpen && !status.cutterError && !status.receiptPaperEmpty) {
            console.log('[Printer] Printer is ready - setting status to true');
            setPrinterIsReadyWithStatus(status);
          } else {
            console.log('[Printer] Printer has issues:', {
              offline: status.offline,
              coverOpen: status.coverOpen,
              cutterError: status.cutterError,
              receiptPaperEmpty: status.receiptPaperEmpty,
            });
            setIsPrinterReady(false);
          }
        } else {
          console.log('[Printer] No printers found');
          setIsPrinterReady(false);
        }
      });

      if (result === null) {
        console.log('[Printer] Skipping periodic check — printer lock held (print in progress)');
      }
    };

    const wrappedConnect = async () => {
      try {
        await connectToPrinter();
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        if (errorMessage.includes('permission') || errorMessage.includes('SecurityException')) {
          console.warn('[Printer] Bluetooth permission denied, skipping printer discovery');
        } else {
          console.error('[Printer] Error connecting to printer:', error);
        }
        setIsPrinterReady(false);
      }
    };

    const handleDeviceReady = () => {
      console.log('[Printer] deviceready event fired');
      wrappedConnect();

      connectionCheckInterval = setInterval(() => {
        console.log('[Printer] Periodic connection check');
        wrappedConnect();
      }, 30000);
    };

    if (document.readyState === 'complete' || (window as any).device) {
      console.log('[Printer] Device already ready, connecting immediately');
      wrappedConnect();

      connectionCheckInterval = setInterval(() => {
        console.log('[Printer] Periodic connection check');
        wrappedConnect();
      }, 30000);
    }

    document.addEventListener('deviceready', handleDeviceReady);

    return () => {
      document.removeEventListener('deviceready', handleDeviceReady);
      if (connectionCheckInterval) {
        clearInterval(connectionCheckInterval);
      }
      try {
        StarPRNT.disconnect();
      } catch (error) {
        console.error('[Printer] Error disconnecting:', error);
      }
    };
  }, [setIsPrinterReady, setPrinterIsReadyWithStatus]);

  return null;
}
